import { saasCatalogModule } from '@agos/saas-catalog'
import { saasFranchisesModule } from '@agos/saas-franchises'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'
import { Route, Switch } from 'wouter'

import { AuditLogPage } from './pages/AuditLogPage'
import { SupportTicketsPage } from './pages/SupportTicketsPage'
import { BillingPage } from './pages/BillingPage'
import { CardPayPage } from './pages/CardPayPage'
import { DashboardPage } from './pages/DashboardPage'
import { ApiPricingPage } from './pages/ApiPricingPage'
import { MarketingPage } from './pages/MarketingPage'
import { PartnersPage } from './pages/PartnersPage'
import { PlatformBillingPage } from './pages/PlatformBillingPage'
import { ProfitabilityPage } from './pages/ProfitabilityPage'
import { StatsPage } from './pages/StatsPage'
import { PartnerPortalPage } from './partner/PartnerPortalPage'
import { UnsavedWorkProvider } from '@agos/shared'

import { TowerShell } from './shell/TowerShell'

/**
 * The Control Tower.
 *
 * **`/partner` is decided before anything else mounts, and that is not a routing preference.** Legacy
 * says why: *"fully isolated from the super-admin auth flow — it uses its own localStorage token and
 * never touches the super-admin context. Render it before mounting AuthProvider so the super-admin
 * `/auth/me` call doesn't fire on the partner login page."*
 *
 * A partner is a different person from a super-admin, with a different token and a different secret
 * server-side. Mounting the portal inside the tower's shell would fire a super-admin session probe on a
 * page no super-admin is looking at — a 401 in someone else's console at best, and at worst a habit of
 * treating the two as one principal.
 *
 * So the check is on `location.pathname` rather than in the `<Switch>`, exactly as legacy has it. It is
 * recorded in `docs/INVENTORY/saas-routes.json` as `mountedIn: "App, before the router"`, because a
 * route diff built by grepping `<Route` would report this path as missing.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A back office is read alongside the thing it describes; a refetch on every focus change moves
      // figures somebody is reading off the screen.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
})

function isPartnerPath(pathname: string): boolean {
  return pathname === '/partner' || pathname.startsWith('/partner/')
}

export function App() {
  const [client] = useState(() => queryClient)
  const pathname = typeof window === 'undefined' ? '' : window.location.pathname

  if (isPartnerPath(pathname)) {
    return (
      <QueryClientProvider client={client}>
        <PartnerPortalPage />
      </QueryClientProvider>
    )
  }

  /* §3.175, FB-941 - the provider sits **above** `TowerShell` rather than inside it, because the shell both
     reads the flag and renders the pages that set it, and a component cannot consume a context it provides in
     the same element. The route list below is deliberately left at its own indentation: `page-titles.test.ts`
     and both route diffs read this file, and a re-indent of forty lines to satisfy nothing is a change those
     would have to be re-read against. */
  return (
    <QueryClientProvider client={client}>
      <UnsavedWorkProvider>
      <TowerShell>
        <Switch>
          <Route path="/" component={DashboardPage} />
          {/* §3.2 - the franchise section is a module now, per the 2026-07-31 rework order. Its own
            * route list keeps `/franchises/new` and the three nested paths ahead of
            * `/franchises/:id`, because `Switch` takes the first match and `new` would otherwise be
            * parsed as an id. */}
          {saasFranchisesModule.routes.map((route) => (
            <Route key={route.path} path={route.path} component={route.component} />
          ))}
          <Route path="/billing" component={BillingPage} />
          {/* §3.3 - the tier catalogue is a module too. */}
          {saasCatalogModule.routes.map((route) => (
            <Route key={route.path} path={route.path} component={route.component} />
          ))}
          <Route path="/stats" component={StatsPage} />
          <Route path="/cardpay" component={CardPayPage} />
          <Route path="/platform-billing" component={PlatformBillingPage} />
          <Route path="/profitability" component={ProfitabilityPage} />
          {/* §3.9 - the partners whose shares `/profitability` pays out, next to it deliberately:
            * the total this screen guards is the one that page's clamp was hiding. */}
          <Route path="/partners" component={PartnersPage} />
          {/* §3.10 - beside `/profitability` for the same reason `/partners` is: this is the screen
            * that says which of that report's costs are simply absent.
            *
            * **`/pricing`, not `/api-pricing`.** The endpoint is `…/super-admin/api-pricing`, and a
            * route of that name is unreachable in this product: vite's proxy key `'/api'` is a
            * **prefix** match and so is Nginx Proxy Manager's `/api` location, so `/api-pricing`
            * goes to the backend and answers *"No static resource api-pricing"*. Caught by the e2e
            * failing at `signedIn` before it reached the page; it would have shipped as a 404 on
            * `hkd.toxik.space`. Any Tower route beginning `/api` has this problem. */}
          <Route path="/pricing" component={ApiPricingPage} />
          <Route path="/marketing" component={MarketingPage} />
          {/* §3.137, owner O31 - every franchise's tickets in one queue. */}
          <Route path="/support-tickets" component={SupportTicketsPage} />
          <Route path="/audit-log" component={AuditLogPage} />
          {/* Legacy redirects an unknown path to `/`. Kept: a back office has no 404 worth writing. */}
          <Route component={DashboardPage} />
        </Switch>
      </TowerShell>
      </UnsavedWorkProvider>
    </QueryClientProvider>
  )
}

export { isPartnerPath }
